
import { FC, useEffect, useMemo } from 'react'
import cn from 'classnames'
import styles from 'components/features/creditCard/creditCardPage/CreditCardLedgerChart/CreditCardLedgerChart.styl'
import { CCLedger, CCPaymentAmountType } from 'components/features/creditCard/creditCardStore/types'
import { useHover } from '@react-aria/interactions'

type CreditCardLedgerChartProps = {
  ledger: CCLedger
  vertical?: boolean
  hideMinimum?: boolean
  getClickedSegment?: (segment: CCPaymentAmountType) => void
  getHoveredSegment?: (segment: CCPaymentAmountType | '') => void
}

const MINIMUM_PAYMENT_WIDTH = '2%'

export const CreditCardLedgerChart: FC<CreditCardLedgerChartProps> = (props) => {
  const { ledger, getClickedSegment, getHoveredSegment, hideMinimum, vertical } = props
  const { credit_limit, current_balance, statement_balance_remaining } = ledger

  /* Calculate width of segments  **/
  /* ====================================================================== **/
  const creditLimitNum = parseInt(credit_limit, 10)
  const currentBalanceNum = parseInt(current_balance, 10)
  const statementBalanceNum = statement_balance_remaining ? parseInt(statement_balance_remaining, 10) : 0
  const getPercentOf = (num: number) => (num / creditLimitNum) * 100
  const currentBalanceWidth = getPercentOf(currentBalanceNum - statementBalanceNum)
  const statementBalanceWidth = getPercentOf(statementBalanceNum)

  const styleProperty = !!vertical ? 'height' : 'width'

  const currentBalanceStyle = useMemo(() => ({
    [styleProperty]: currentBalanceWidth + '%',
  }), [currentBalanceWidth, styleProperty])

  const statementBalanceStyle = useMemo(() => ({
    [styleProperty]: statementBalanceWidth + '%',
  }), [statementBalanceWidth, styleProperty])

  const minimumBalanceStyle = useMemo(() => ({
    [styleProperty]: currentBalanceWidth < 2 ? '1%' : MINIMUM_PAYMENT_WIDTH,
  }), [currentBalanceWidth, styleProperty])


  /* Hover Functionality:
  /  A parent component can react to which segment is hovered
  /* ====================================================================== **/

  const currentBalanceHover = useHover({})
  const statementBalanceHover = useHover({})
  const minimumBalanceHover = useHover({})

  useEffect(() => {
    if (getHoveredSegment) {
      if (currentBalanceHover.isHovered) {
        getHoveredSegment('current')
      } else if (statementBalanceHover.isHovered) {
        getHoveredSegment('statement')
      } else if (minimumBalanceHover.isHovered) {
        getHoveredSegment('minimum')
      } else {
        getHoveredSegment('')
      }
    }
  })

  /* PRE-RENDER  **/
  /* ====================================================================== **/

  const currentBalanceChild = useMemo(() => !!currentBalanceWidth && (
    <div
      { ...currentBalanceHover.hoverProps }
      className={ cn(!minimumBalanceHover.isHovered && styles.currentBalance) }
      style={ currentBalanceStyle }
      onClick={ () => !!getClickedSegment && getClickedSegment('current') }
    />
  ), [currentBalanceWidth, currentBalanceHover, minimumBalanceHover, currentBalanceStyle, getClickedSegment])

  const statementBalanceChild = useMemo(() => !!statementBalanceWidth && (
    <div
      { ...statementBalanceHover.hoverProps }
      className={ cn(!minimumBalanceHover.isHovered && styles.statementBalance) }
      style={ statementBalanceStyle }
      onClick={ () => !!getClickedSegment && getClickedSegment('statement') }
    />
  ), [statementBalanceWidth, statementBalanceHover, minimumBalanceHover.isHovered, statementBalanceStyle, getClickedSegment])

  const minimumAmountChild = useMemo(() => !hideMinimum && (
    <div
      { ...minimumBalanceHover.hoverProps }
      className={ styles.minimumAmount }
      style={ minimumBalanceStyle }
      onClick={ () => !!getClickedSegment && getClickedSegment('minimum') }
    />
  ), [hideMinimum, minimumBalanceHover.hoverProps, minimumBalanceStyle, getClickedSegment])

  const classNames = cn(
    styles.creditCardLedgerChart, {
      [styles.vertical]: !!vertical,
      [styles.clickable]: !!getClickedSegment,
      [styles.currentBalance]: !!currentBalanceHover.isHovered,
      [styles.statementBalance]: !!statementBalanceHover.isHovered,
    }
  )

  /* RENDER  **/
  /* ====================================================================== **/

  return (
    <div className={ classNames }>
      { minimumAmountChild }
      { statementBalanceChild }
      { currentBalanceChild }
    </div>
  )
}
